import _ from 'lodash';
import moment from 'moment';
import router from '@/router';
import phoneNumberUtils from '@/utils/phoneNumber';
import { PerformanceMarks, performanceService } from '@/utils/PerformanceService';
import cashflowModelsApi from '@/api/CashflowModels';
import { getPlatformInfo } from '@/utils/deviceUtils';
import Segment from '../../Segment';
import Intercom from '../../Intercom';
import Sentry from '../../Sentry';
import DDLogs from '../../DDLogs';
import CustomerApi from '../../api/CustomersApi';
import CustomerActivityApi from '../../api/CustomerActivityApi';
import HansApi from '../../api/HansApi';

const state = {
  authenticated: false,
  inviteToken: '',
  customerId: null,
  members: [],
  activeMember: null,
  activeMemberPhoneNumber: null,
  isRestrictedCustomer: false,
  segmentEventPrefix: '',
  _sessionData: null,
};

const getters = {
  firstName: (state, getters) => getters.primaryMember.firstNamePiiValue,
  activeMemberFirstName: state => state.activeMember.firstNamePiiValue,
  activeMemberLastName: state => state.activeMember.lastNamePiiValue,
  activeMemberFullName: state => `${state.activeMember.firstNamePiiValue} ${state.activeMember.lastNamePiiValue}`,
  activeMemberPhoneNumber: state => phoneNumberUtils.visualizePhoneNumber(state.activeMemberPhoneNumber),
  activeMemberEmail: state => state.activeMember.emailPiiValue,
  numberOfMembers: state => state.members.length,
  householdName: state => {
    const customerPrimaryMember = _.find(state.members, m => m.primary);
    const secondMemberCreated = _.chain(state.members)
      .filter(m => !m.primary)
      .sortBy(m => m.created)
      .first()
      .value();
    return _.compact([
      customerPrimaryMember && customerPrimaryMember.firstNamePiiValue,
      secondMemberCreated && secondMemberCreated.firstNamePiiValue,
    ]).join(' ו');
  },
  primaryMember: state => {
    const primaryMember = _.find(state.members, { primary: true }) || {};
    return {
      firstNamePiiValue: primaryMember.firstNamePiiValue,
      lastNamePiiValue: primaryMember.lastNamePiiValue,
      phoneNumberPiiValue: phoneNumberUtils.visualizePhoneNumber(primaryMember.phoneNumberPiiValue),
    };
  },
  memberIsPrimary: state => state.activeMember.primary === true,
  isMultipleMembersAccount: state => state.members.length > 1,
  otherMemberFirstName: state => _.chain(state.members)
    .reject(m => m.firstNamePiiValue === state.activeMember.firstNamePiiValue)
    .map('firstNamePiiValue')
    .first()
    .value(),
  otherMembers: state => _.chain(state.members)
    .reject(m => m.firstNamePiiValue === state.activeMember.firstNamePiiValue)
    .map(member => ({ name: member.firstNamePiiValue, phone: member.phoneNumberPiiValue }))
    .value(),

  // eslint-disable-next-line max-len
  inviteMessage: () => 'היי! *נרשמתי ל-riseup!*\nאני מחכה לך בתזרים המשותף שלנו 🎯\nכל מה שצריך לעשות בשביל להצטרף אליי זה *ללחוץ על הלינק המצורף* 👇\n',
};

const actions = {
  async initRiseupAccount({ commit }, phoneNumber) {
    const members = await CustomerApi.getMembers();
    const activeMember = members.find(m => phoneNumberUtils.comparePhoneNumbers(m.phoneNumberPiiValue, phoneNumber));
    commit('setActiveMember', activeMember);
    commit('setAuthenticated', true);
  },
  async initOts({ commit }) {
    try {
      const { restrictedCustomerData: customerDetails } = await CustomerApi.getCustomerSessionData();
      commit('setCustomerId', customerDetails.customerId);
      commit('setAuthenticated', true);
    } catch (e) {
      DDLogs.error('Error initializing ots session', e);
      commit('setAuthenticated', false);
    }
  },
  // Minimal session init — only what's needed before the page renders
  async initMinimal({ commit, getters, state }, { incognito } = {}) {
    try {
      const sessionData = await CustomerApi.getCustomerSessionData();
      const { restrictedCustomerData: customerDetails, members, activeMember, onboardingStatus } = sessionData;
      commit('setSessionData', sessionData);
      commit('setActiveMember', activeMember);
      commit('setActiveMemberPhoneNumber', activeMember.phoneNumberPiiValue);
      commit('setCustomerId', customerDetails.customerId);
      commit('setAuthenticated', true);
      commit('setIsRestrictedCustomer', customerDetails.isRestrictedCustomer);
      commit('setMembers', members);
      DDLogs.addGlobalContext('customerId', customerDetails.customerId);
      if (!incognito) {
        DDLogs.info('[Intercom Debug] Booting Intercom');
        try {
          Intercom.boot(customerDetails.customerId, getters.householdName, {
            userHash: customerDetails.intercomUserHash,
            isOnboarding: onboardingStatus !== 'completed',
          });
        } catch (intercomError) {
          DDLogs.error('Error booting intercom', intercomError);
        }
      }
    } catch (e) {
      DDLogs.error('Error initializing minimal session', e);
      commit('setAuthenticated', false);
      commit('setSessionData', null);
      if (e.response && e.response.status === 403) {
        // Added this in order to avoid sentry issues with 403 errors
        console.log('Could not authenticate', e);
      }
    }
  },
  // Full session init — runs in background after page renders
  async initDeferred({ commit, getters, dispatch, rootGetters, rootState, state }, { incognito, name, segmentEventPrefix }) {
    try {
      performanceService.markStart(PerformanceMarks.INIT_SESSION);
      const [{ restrictedCustomerData: customerDetails, members },
        invalidCredsState, churnPredictionData] = await Promise.all([
        state._sessionData ? Promise.resolve(state._sessionData) : CustomerApi.getCustomerSessionData(),
        CustomerApi.getCustomerState().then(async consolidatedResponse => {
          const { onboardingState, invalidCredentialsState, timeLimitedOffers, simplifiedSubscriptionState } = consolidatedResponse;
          await Promise.all(_.compact([
            onboardingState && dispatch('onboardingState/setOnboardingState', onboardingState, { root: true }),
            dispatch('subscriptionState/setSimplifiedSubscriptionState', simplifiedSubscriptionState, { root: true }),
            commit('timeLimitedOffers/setTimeLimitedOffers', timeLimitedOffers, { root: true }),
          ]));
          return invalidCredentialsState;
        }),
        cashflowModelsApi.getChurnPrediction(),
      ]);
      const { activatedAt } = rootState.onboardingState;

      // Ensure feature flags are loaded before logging treatments
      await dispatch('featureFlags/init', null, { root: true });
      dispatch('featureFlags/logTreatments', customerDetails.customerId, { root: true });
      commit('setSegmentEventPrefix', segmentEventPrefix);

      const loggedInMember = state.activeMember || _.find(members, { primary: true });
      if (loggedInMember && loggedInMember.firstNamePiiValue) {
        try {
          localStorage.setItem('riseup:returningCustomerFirstName', loggedInMember.firstNamePiiValue);
        } catch (e) { /* storage unavailable */ }
      }

      Segment.setIncognito(incognito);
      Segment.identifyCustomer(customerDetails.customerId);
      const isOtsIsrael = rootGetters['onboardingState/isOTSIL'];

      if (!incognito) {
        if (!isOtsIsrael) {
          // Update Intercom with full attributes (boot already happened in initMinimal)
          try {
            const primaryMember = _.find(members, { primary: true });
            const { modelScore, behaviorHighlights, financialHighlights, personHighlights } = churnPredictionData;
            const { platform, appVersion } = getPlatformInfo();
            Intercom.update(customerDetails.customerId, getters.householdName, {
              activatedAt,
              daysInInvalidCredentials: invalidCredsState ? Math.abs(moment(invalidCredsState.since).diff(moment(), 'days')) : 0,
              email: primaryMember ? primaryMember.emailPiiValue : null,
              phone: primaryMember.phoneNumberPiiValue,
              firstName1: primaryMember.firstNamePiiValue,
              numberOfMembers: getters.numberOfMembers,
              mfaEnabled: _.every(state.members, 'oidc'),
              modelScore,
              behaviorHighlights,
              financialHighlights,
              personHighlights,
              user_platform: platform,
              appVersion,
              ...rootGetters['featureFlags/featureFlagAttributes'],
            });
          } catch (intercomError) {
            DDLogs.error('Error updating Intercom in initDeferred', intercomError);
          }
        }
        Sentry.initCustomerId(customerDetails.customerId);
        dispatch('addCustomerActivity', name);
      }
    } catch (e) {
      DDLogs.error('Error initializing deferred session', e);
    } finally {
      performanceService.markEnd(PerformanceMarks.INIT_SESSION);
    }
  },
  // Original init — kept for backward compatibility with other callers
  async init({ dispatch }, params) {
    await dispatch('initMinimal', { incognito: params?.incognito });
    await dispatch('initDeferred', params);
  },
  async initInviteToken({ commit, getters }) {
    if (getters.memberIsPrimary) {
      const { token } = await HansApi.generateInviteToken();
      commit('setInviteToken', token);
    }
  },
  async updateActiveMemberEmail({ dispatch }, email) {
    await CustomerApi.updateActiveMemberEmail(email);
    const { incognito } = router.history.current.query;
    // calling init again to get the updated info and report the new email to intercom
    await dispatch('init', { incognito });
  },
  async addCustomerActivity({ state }, pageName) {
    if (!state.activeMember) {
      DDLogs.error('Session state might not be initialized properly - no active member found');
      return;
    }
    await CustomerActivityApi.setCustomerActivity({
      activityTime: moment(),
      details: {
        activityType: 'visitPage',
        pageName,
        memberId: state.activeMember._id,
      },
    });
  },
};

const mutations = {
  setAuthenticated(state, authenticateRespond) {
    state.authenticated = authenticateRespond;
  },
  setActiveMember(state, member) {
    state.activeMember = member;
  },
  setActiveMemberPhoneNumber(state, phoneNumber) {
    state.activeMemberPhoneNumber = phoneNumber;
  },
  setCustomerId(state, customerId) {
    state.customerId = customerId;
  },
  setMembers(state, members) {
    state.members = members;
  },
  setInviteToken(state, inviteToken) {
    state.inviteToken = inviteToken;
  },
  setIsRestrictedCustomer(state, isRestrictedCustomer) {
    state.isRestrictedCustomer = isRestrictedCustomer;
  },
  setSegmentEventPrefix(state, segmentEventPrefix) {
    state.segmentEventPrefix = segmentEventPrefix;
  },
  setSessionData(state, sessionData) {
    state._sessionData = sessionData;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
